<script lang="ts" setup>
import ChipStack from "@/components/table/chips/ChipStack.vue";

interface Winner {
  translate: { x: number; y: number };
  payout: number;
}
interface Props {
  appViewMode: string;
  pot: number;
  winners: Winner[];
}

const props = defineProps<Props>();

const WINNER_POT_MOVE_DURATION_SEC = 0.7;

const onLeave = (el: HTMLElement, done: () => void) => {
  el.style.opacity = "0";
  done();
  return;
};
const onBeforeEnterGroup = (el: HTMLElement) => {
  Object.assign(el.style, {
    left: "50%",
    position: "absolute",
    top: "0",
    transform: "translateX(-50%)",
    transition: `all ${WINNER_POT_MOVE_DURATION_SEC}s ease`,
  });
};
const onEnterGroup = (el: HTMLElement, done: () => void) => {
  try {
    const indexAttr = el.dataset.index;
    const winner = props.winners[indexAttr];
    
    if (!winner) {
      done();
      return;
    }
    
    const { x, y } = winner.translate;

    requestAnimationFrame(() => {
      el.style.opacity = "1";
      el.style.transform = `translate(${x}px, ${y}px)`;
    });

    done();
  } catch (e) {
    console.warn('Animation error:', e);
    done();
  }
};
</script>

<template>
  <div 
    :class="`table__total-pot table__total-pot--${appViewMode}`"
    aria-live="polite"
    :aria-label="`Total pot: ${pot}. ${winners.length} winner${winners.length !== 1 ? 's' : ''}`"
  >
    <Transition mode="out-in" @leave="onLeave">
      <ChipStack
        v-if="pot > 0"
        :class="`table__total-pot__chips table__total-pot__chips--${appViewMode}`"
        style="white-space: nowrap"
        :value="pot"
        :variant="appViewMode === 'mobile' ? 'dark' : 'light'"
        :aria-label="`Total pot amount: ${pot}`"
      />
    </Transition>
    <TransitionGroup
      tag="div"
      class="chip-stack-group"
      @before-enter="onBeforeEnterGroup"
      @enter="onEnterGroup"
      :css="false"
      aria-live="polite"
      :aria-label="`Winner payouts: ${winners.map(w => w.payout).join(', ')}`"
    >
      <ChipStack
        v-for="(winner, idx) in winners"
        :key="idx"
        :value="winner.payout"
        :class="`table__total-pot__chips table__total-pot__chips--${appViewMode}`"
        style="white-space: nowrap"
        :variant="appViewMode === 'mobile' ? 'dark' : 'light'"
        layout="vertical"
        :data-index="idx"
        :aria-label="`Winner ${idx + 1} payout: ${winner.payout}`"
      />
    </TransitionGroup>
  </div>
</template>

<style lang="scss" scoped>
.table__total-pot {
  z-index: 9;
}
.chip-stack-group {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  :deep(.chip-stack-enter-from .chip-stack__stack-chip) {
    transform: none;
  }
}
:deep(.chip-stack--vertical) {
  .chip-stack__value {
    margin-bottom: 4px;
  }
}
</style>
